/**
 * src/pages/admin/AdminActivityLog.js
 * 
 * Full audit trail of admin actions.
 * Real-time listener on admin_activity_log collection.
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  Activity,
  Shield,
  ShieldCheck,
  Bell,
  MessageSquare,
  ShieldAlert,
  Ban,
  CheckCircle2,
  Settings,
  Calendar,
  Trash2,
  CheckSquare,
  Square,
} from "lucide-react";
import * as adminService from "../../services/adminService";
import { useToast } from "../../contexts/ToastContext";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import { useAdmin } from "../../hooks/useAdmin";

function formatTimestamp(ts) {
  if (!ts) return "";
  const d = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
  return d.toLocaleDateString("en", {
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

const ACTION_CONFIG = {
  // Uppercase keys (from logAdminAction)
  UPDATE_USER_ROLE:      { Icon: Shield,      color: "text-purple-500",  bg: "bg-purple-100 dark:bg-purple-900/30" },
  BAN_USER:              { Icon: Ban,         color: "text-red-500",     bg: "bg-red-100 dark:bg-red-900/30" },
  UNBAN_USER:            { Icon: CheckCircle2,color: "text-green-500",   bg: "bg-green-100 dark:bg-green-900/30" },
  SEND_NOTIFICATION:     { Icon: Bell,        color: "text-blue-500",    bg: "bg-blue-100 dark:bg-blue-900/30" },
  DEACTIVATE_NOTIFICATION: { Icon: Bell,      color: "text-gray-500",    bg: "bg-gray-100 dark:bg-neutral-700" },
  MODERATE_PRODUCT:      { Icon: ShieldAlert, color: "text-orange-500",  bg: "bg-orange-100 dark:bg-orange-900/30" },
  REPLY_TICKET:          { Icon: MessageSquare,color: "text-cyan-500",   bg: "bg-cyan-100 dark:bg-cyan-900/30" },
  UPDATE_TICKET:         { Icon: Settings,    color: "text-gray-500",    bg: "bg-gray-100 dark:bg-neutral-700" },
  UPDATE_FEEDBACK_STATUS:{ Icon: MessageSquare,color: "text-teal-500",   bg: "bg-teal-100 dark:bg-teal-900/30" },
  // Lowercase keys (backward compat / legacy)
  role_change:           { Icon: Shield,      color: "text-purple-500",  bg: "bg-purple-100 dark:bg-purple-900/30" },
  user_ban:              { Icon: Ban,         color: "text-red-500",     bg: "bg-red-100 dark:bg-red-900/30" },
  user_unban:            { Icon: CheckCircle2,color: "text-green-500",   bg: "bg-green-100 dark:bg-green-900/30" },
  notification:          { Icon: Bell,        color: "text-blue-500",    bg: "bg-blue-100 dark:bg-blue-900/30" },
  moderation:            { Icon: ShieldAlert, color: "text-orange-500",  bg: "bg-orange-100 dark:bg-orange-900/30" },
  ticket_reply:          { Icon: MessageSquare,color: "text-cyan-500",   bg: "bg-cyan-100 dark:bg-cyan-900/30" },
  ticket_status:         { Icon: Settings,    color: "text-gray-500",    bg: "bg-gray-100 dark:bg-neutral-700" },
  feedback:              { Icon: MessageSquare,color: "text-teal-500",   bg: "bg-teal-100 dark:bg-teal-900/30" },
  default:               { Icon: Activity,    color: "text-gray-400",    bg: "bg-gray-100 dark:bg-neutral-700" },
};

const ACTION_TYPES = [
  { id: "all",                    label: "All" },
  { id: "UPDATE_USER_ROLE",       label: "Roles" },
  { id: "BAN_USER",               label: "Bans" },
  { id: "SEND_NOTIFICATION",      label: "Notifications" },
  { id: "MODERATE",               label: "Moderation" },
  { id: "REPLY_TICKET",           label: "Tickets" },
];

function LogEntry({ entry }) {
  const cfg = ACTION_CONFIG[entry.action] || ACTION_CONFIG.default;
  const Ic = cfg.Icon;

  return (
    <div className="flex items-start gap-3 py-3 border-b border-gray-100 dark:border-neutral-800 last:border-0">
      <div className={`w-8 h-8 rounded-full ${cfg.bg} flex items-center justify-center flex-shrink-0`}>
        <Ic className={`w-4 h-4 ${cfg.color}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-800 dark:text-gray-200">
          <span className="font-medium">{entry.adminName || entry.adminEmail || "Admin"}</span>
          <span className="text-gray-500 dark:text-gray-400"> {entry.humanDescription || entry.description || entry.action}</span>
        </p>
        {entry.detailLine && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
            {entry.detailLine}
          </p>
        )}
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          {formatTimestamp(entry.createdAt || entry.timestamp)}
        </p>
      </div>
    </div>
  );
}

export default function AdminActivityLog() {
  const toast = useToast();
  const { user: adminUser } = useAdmin();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState([]);
  const [dialog, setDialog] = useState({ open: false, title: "", message: "", loading: false, action: null });

  useEffect(() => {
    const unsub = adminService.listenActivityLog((data) => {
      setLogs(data);
      setLoading(false);
    }, 100);
    return () => unsub?.();
  }, []);

  const filtered = filter === "all"
    ? logs
    : logs.filter((l) => l.action === filter || l.action?.startsWith(filter));

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const allVisibleSelected = filtered.length > 0 && filtered.every((entry) => selectedSet.has(entry.id));

  useEffect(() => {
    setSelected((prev) => prev.filter((id) => logs.some((entry) => entry.id === id)));
  }, [logs]);

  const toggleRow = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  const toggleAllVisible = () => {
    if (allVisibleSelected) {
      setSelected((prev) => prev.filter((id) => !filtered.some((entry) => entry.id === id)));
      return;
    }

    const visibleIds = filtered.map((entry) => entry.id);
    setSelected((prev) => Array.from(new Set([...prev, ...visibleIds])));
  };

  const runDialogAction = async () => {
    if (!dialog.action) return;
    setDialog((prev) => ({ ...prev, loading: true }));
    try {
      await dialog.action();
      setDialog({ open: false, title: "", message: "", loading: false, action: null });
    } catch (err) {
      toast.error(err?.message || "Action failed");
      setDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  const confirmDeleteSingle = (entry) => {
    setDialog({
      open: true,
      title: "Delete log entry",
      message: "This log entry will be permanently removed.",
      loading: false,
      action: async () => {
        await adminService.withAdminRetry(() => adminService.deleteActivityLogEntry(adminUser, entry.id));
        setSelected((prev) => prev.filter((id) => id !== entry.id));
        toast.success("Log entry deleted");
      },
    });
  };

  const confirmDeleteBulk = () => {
    if (!selected.length) return;
    setDialog({
      open: true,
      title: "Delete selected logs",
      message: `Delete ${selected.length} selected log entries?`,
      loading: false,
      action: async () => {
        const ids = [...selected];
        await adminService.withAdminRetry(() => adminService.deleteActivityLogEntries(adminUser, ids));
        setSelected([]);
        toast.success(`${ids.length} log entries deleted`);
      },
    });
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3 h-14">
            <Activity className="w-5 h-5 text-green-600 dark:text-green-400" />
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">
              Activity Log
            </h1>
            <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto">
              {filtered.length} entries
            </span>
          </div>

          <div className="flex items-center justify-between pb-2">
            <button
              onClick={toggleAllVisible}
              className="inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 hover:text-green-700 dark:hover:text-green-400"
            >
              {allVisibleSelected ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
              {allVisibleSelected ? "Clear selection" : "Select visible"}
            </button>
            {selected.length > 0 && (
              <button
                onClick={confirmDeleteBulk}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs font-medium hover:bg-red-200"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Delete selected ({selected.length})
              </button>
            )}
          </div>

          {/* Filter Chips */}
          <div className="flex gap-1 pb-3 overflow-x-auto scrollbar-hide">
            {ACTION_TYPES.map((at) => (
              <button
                key={at.id}
                onClick={() => setFilter(at.id)}
                className={`
                  px-3 py-1.5 rounded-full text-xs font-medium border transition whitespace-nowrap
                  ${filter === at.id
                    ? 'bg-green-600 text-white border-green-600'
                    : 'border-gray-200 dark:border-neutral-700 text-gray-600 dark:text-gray-300 hover:border-green-500'
                  }
                `}
              >
                {at.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5 pb-24">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="orrith-loader orrith-loader-md orrith-loader-primary" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <Activity className="w-12 h-12 text-gray-300 dark:text-neutral-600 mx-auto mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No activity recorded yet
            </p>
          </div>
        ) : (
          <div className="bg-white dark:bg-neutral-800 rounded-xl border border-gray-200 dark:border-neutral-700 divide-y-0 px-4">
            {filtered.map((entry) => (
              <div key={entry.id} className="flex items-start gap-2">
                <button
                  onClick={() => toggleRow(entry.id)}
                  className="mt-4 text-gray-400 hover:text-green-600"
                  aria-label={selectedSet.has(entry.id) ? "Unselect log" : "Select log"}
                >
                  {selectedSet.has(entry.id) ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                </button>

                <div className="flex-1 min-w-0">
                  <LogEntry entry={entry} />
                </div>

                <button
                  onClick={() => confirmDeleteSingle(entry)}
                  className="mt-4 inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={dialog.open}
        title={dialog.title}
        message={dialog.message}
        confirmLabel="Delete"
        destructive={true}
        loading={dialog.loading}
        onConfirm={runDialogAction}
        onCancel={() => {
          if (dialog.loading) return;
          setDialog({ open: false, title: "", message: "", loading: false, action: null });
        }}
      />
    </div>
  );
}
